import { createServerFn } from "@tanstack/react-start";
import { eq, asc, and, inArray } from "drizzle-orm";
import { z } from "zod";
import { getDb, schema } from "../db/client.server";
import { requirePermission } from "../auth/session.server";
import { generateUniqueSlug } from "../utils/slug.server";
import { invalidateServicesCache } from "./public.functions";
import { mediaIdSchema, resolveImageMediaInput } from "../media/input.server";

const categoryInput = z.object({
  name: z.string().min(1),
  slug: z.string().optional(),
  short: z.string().optional(),
  intro: z.string().optional(),
  imageMediaId: mediaIdSchema,
  imageUrl: z.string().optional().nullable(),
  iconName: z.string().optional().nullable(),
  sortOrder: z.number().optional(),
  status: z.enum(["draft", "published", "archived"]).default("published"),
  items: z
    .array(
      z.object({
        id: z.string().uuid().optional(),
        name: z.string(),
        description: z.string().optional(),
        sortOrder: z.number().optional(),
        imageMediaId: mediaIdSchema,
        imageUrl: z.string().optional().nullable(),
      }),
    )
    .optional(),
});

export const listServiceCategoriesAdmin = createServerFn({ method: "GET" }).handler(async () => {
  await requirePermission("services", "read");
  const db = getDb();
  const categories = await db.select().from(schema.serviceCategories).orderBy(asc(schema.serviceCategories.sortOrder));
  return Promise.all(
    categories.map(async (cat) => {
      const items = await db.select().from(schema.serviceItems).where(eq(schema.serviceItems.categoryId, cat.id)).orderBy(asc(schema.serviceItems.sortOrder));
      return { ...cat, items };
    }),
  );
});

export const getServiceCategoryAdmin = createServerFn({ method: "GET" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requirePermission("services", "read");
    const db = getDb();
    const [cat] = await db.select().from(schema.serviceCategories).where(eq(schema.serviceCategories.id, data.id)).limit(1);
    if (!cat) return null;
    const items = await db.select().from(schema.serviceItems).where(eq(schema.serviceItems.categoryId, cat.id)).orderBy(asc(schema.serviceItems.sortOrder));
    return { ...cat, items };
  });

export const saveServiceCategory = createServerFn({ method: "POST" })
  .inputValidator(categoryInput.extend({ id: z.string().uuid().optional() }))
  .handler(async ({ data }) => {
    await requirePermission("services", "write");
    const db = getDb();
    const slug = data.slug || (data.id ? undefined : await generateUniqueSlug(schema.serviceCategories, data.name));
    let categoryId = data.id;
    const image = await resolveImageMediaInput(data);

    if (categoryId) {
      const [existing] = await db.select().from(schema.serviceCategories).where(eq(schema.serviceCategories.id, categoryId)).limit(1);
      if (!existing) throw new Error("Not found");
      await db.update(schema.serviceCategories).set({
        name: data.name,
        slug: slug ?? existing.slug,
        short: data.short,
        intro: data.intro,
        imageMediaId: image.imageMediaId,
        imageUrl: image.imageUrl,
        iconName: data.iconName ?? existing.iconName,
        sortOrder: data.sortOrder ?? existing.sortOrder,
        status: data.status,
        updatedAt: new Date(),
      }).where(eq(schema.serviceCategories.id, categoryId));
    } else {
      const [inserted] = await db.insert(schema.serviceCategories).values({
        name: data.name,
        slug: slug!,
        short: data.short,
        intro: data.intro,
        imageMediaId: image.imageMediaId,
        imageUrl: image.imageUrl,
        iconName: data.iconName ?? null,
        sortOrder: data.sortOrder ?? 0,
        status: data.status,
      }).returning();
      categoryId = inserted.id;
    }

    if (data.items) {
      await db.delete(schema.serviceItems).where(eq(schema.serviceItems.categoryId, categoryId!));
      for (let i = 0; i < data.items.length; i++) {
        const item = data.items[i];
        const itemImage = await resolveImageMediaInput({
          imageMediaId: item.imageMediaId,
          imageUrl: item.imageUrl,
        });
        await db.insert(schema.serviceItems).values({
          categoryId: categoryId!,
          name: item.name,
          description: item.description,
          imageMediaId: itemImage.imageMediaId,
          imageUrl: itemImage.imageUrl,
          sortOrder: item.sortOrder ?? i,
        });
      }
    }

    await invalidateServicesCache();
    const [cat] = await db.select().from(schema.serviceCategories).where(eq(schema.serviceCategories.id, categoryId!)).limit(1);
    const items = await db.select().from(schema.serviceItems).where(eq(schema.serviceItems.categoryId, categoryId!)).orderBy(asc(schema.serviceItems.sortOrder));
    return { ...cat!, items };
  });

export const deleteServiceCategory = createServerFn({ method: "POST" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requirePermission("services", "delete");
    await getDb().delete(schema.serviceCategories).where(eq(schema.serviceCategories.id, data.id));
    await invalidateServicesCache();
    return { ok: true };
  });

export const reorderServiceCategories = createServerFn({ method: "POST" })
  .inputValidator(z.object({ ids: z.array(z.string().uuid()).min(1) }))
  .handler(async ({ data }) => {
    await requirePermission("services", "write");
    const db = getDb();
    for (let i = 0; i < data.ids.length; i++) {
      await db
        .update(schema.serviceCategories)
        .set({ sortOrder: i, updatedAt: new Date() })
        .where(and(eq(schema.serviceCategories.id, data.ids[i]), inArray(schema.serviceCategories.id, data.ids)));
    }
    await invalidateServicesCache();
    return { ok: true };
  });

export const generateServiceCategorySlug = createServerFn({ method: "POST" })
  .inputValidator(z.object({ name: z.string(), excludeId: z.string().uuid().optional() }))
  .handler(async ({ data }) => {
    await requirePermission("services", "read");
    return generateUniqueSlug(schema.serviceCategories, data.name, data.excludeId);
  });
